import axios from "axios";
import {
  AuthResponse,
  User,
  BankAccount,
  Transaction,
  TransactionStats,
  AccountSummary,
  Client,
  ClientTransaction,
  ClientStats,
  Company,
  CompanySalaryTransaction,
  CompanySalarySummary,
  InvestmentPlatform,
  Investment,
  InvestmentStats,
} from "../types";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["x-auth-token"] = token;
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    
    // Enhance error message
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timeout. Please check your connection and try again.';
    } else if (error.code === 'ERR_NETWORK') {
      error.message = 'Network error. Please check your internet connection.';
    } else if (!error.response) {
      error.message = 'Unable to connect to server. Please try again later.';
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (
    name: string,
    username: string,
    email: string,
    password: string,
  ): Promise<AuthResponse> => {
    const response = await api.post("/auth/register", {
      name,
      username,
      email,
      password,
    });
    return response.data;
  },

  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post("/auth/login", { email, password });
    return response.data;
  },

  getUser: async (): Promise<User> => {
    const response = await api.get("/auth/user");
    return response.data;
  },
};

// Bank Accounts API
export const accountsAPI = {
  getAccounts: async (): Promise<BankAccount[]> => {
    const response = await api.get("/accounts");
    return response.data;
  },

  getAccount: async (id: string): Promise<BankAccount> => {
    const response = await api.get(`/accounts/${id}`);
    return response.data;
  },

  createAccount: async (data: {
    bankName: string;
    accountNumber: string;
    balance?: number;
  }): Promise<BankAccount> => {
    const response = await api.post("/accounts", data);
    return response.data;
  },

  updateAccount: async (
    id: string,
    data: { bankName?: string; accountNumber?: string },
  ): Promise<BankAccount> => {
    const response = await api.put(`/accounts/${id}`, data);
    return response.data;
  },

  deleteAccount: async (id: string): Promise<void> => {
    await api.delete(`/accounts/${id}`);
  },

  getAccountsSummary: async (): Promise<AccountSummary> => {
    const response = await api.get("/accounts/summary/total");
    return response.data;
  },
};

// Transactions API
export const transactionsAPI = {
  getTransactions: async (params?: {
    account?: string;
    category?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    page?: number;
  }): Promise<{ transactions: Transaction[]; pagination: any }> => {
    const response = await api.get("/transactions", { params });
    return response.data;
  },

  getTransaction: async (id: string): Promise<Transaction> => {
    const response = await api.get(`/transactions/${id}`);
    return response.data;
  },

  createTransaction: async (data: {
    account: string;
    type: "credit" | "debit";
    amount: number;
    description: string;
    category?: string;
    date?: string;
  }): Promise<Transaction> => {
    const response = await api.post("/transactions", data);
    return response.data;
  },

  updateTransaction: async (
    id: string,
    data: {
      type?: "credit" | "debit";
      amount?: number;
      description?: string;
      category?: string;
      date?: string;
    },
  ): Promise<Transaction> => {
    const response = await api.put(`/transactions/${id}`, data);
    return response.data;
  },

  deleteTransaction: async (id: string): Promise<void> => {
    await api.delete(`/transactions/${id}`);
  },

  getTransactionStats: async (params?: {
    startDate?: string;
    endDate?: string;
    account?: string;
  }): Promise<TransactionStats> => {
    const response = await api.get("/transactions/summary/stats", { params });
    return response.data;
  },
};

// Clients API
export const clientsAPI = {
  getClients: async (params?: {
    status?: string;
    search?: string;
  }): Promise<Client[]> => {
    const response = await api.get("/clients", { params });
    return response.data;
  },

  getClient: async (id: string): Promise<Client> => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  createClient: async (data: {
    name: string;
    email?: string;
    phone?: string;
    address?: any;
    company?: string;
    notes?: string;
  }): Promise<Client> => {
    const response = await api.post("/clients", data);
    return response.data;
  },

  updateClient: async (
    id: string,
    data: {
      name?: string;
      email?: string;
      phone?: string;
      address?: any;
      company?: string;
      notes?: string;
      status?: string;
    },
  ): Promise<Client> => {
    const response = await api.put(`/clients/${id}`, data);
    return response.data;
  },

  deleteClient: async (id: string): Promise<void> => {
    await api.delete(`/clients/${id}`);
  },

  getClientStats: async (): Promise<ClientStats> => {
    const response = await api.get("/clients/summary/stats");
    return response.data;
  },
};

// Client Transactions API
export const clientTransactionsAPI = {
  getClientTransactions: async (params?: {
    client?: string;
    type?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    page?: number;
  }): Promise<{ transactions: ClientTransaction[]; pagination: any }> => {
    const response = await api.get("/client-transactions", { params });
    return response.data;
  },

  getClientTransaction: async (id: string): Promise<ClientTransaction> => {
    const response = await api.get(`/client-transactions/${id}`);
    return response.data;
  },

  createClientTransaction: async (data: {
    client: string;
    type: "given" | "taken";
    amount: number;
    description: string;
    paymentMethod?: string;
    referenceNumber?: string;
    dueDate?: string;
    category?: string;
    notes?: string;
    date?: string;
  }): Promise<ClientTransaction> => {
    const response = await api.post("/client-transactions", data);
    return response.data;
  },

  updateClientTransaction: async (
    id: string,
    data: {
      type?: "given" | "taken";
      amount?: number;
      description?: string;
      paymentMethod?: string;
      referenceNumber?: string;
      dueDate?: string;
      category?: string;
      status?: string;
      notes?: string;
      date?: string;
    },
  ): Promise<ClientTransaction> => {
    const response = await api.put(`/client-transactions/${id}`, data);
    return response.data;
  },

  deleteClientTransaction: async (id: string): Promise<void> => {
    await api.delete(`/client-transactions/${id}`);
  },

  getClientTransactionsByClient: async (
    clientId: string,
    params?: {
      limit?: number;
      page?: number;
    },
  ): Promise<{ transactions: ClientTransaction[]; pagination: any }> => {
    const response = await api.get(`/client-transactions/client/${clientId}`, {
      params,
    });
    return response.data;
  },
};

// Companies API
export const companiesAPI = {
  getCompanies: async (): Promise<Company[]> => {
    const response = await api.get("/company");
    return response.data;
  },

  getCompany: async (id: string): Promise<Company> => {
    const response = await api.get(`/company/${id}`);
    return response.data;
  },

  createCompany: async (data: {
    name: string;
    ctc: number;
    base: number;
    medical?: number;
    joiningBonus?: number;
    relocationBonus?: number;
    otherBenefits?: number;
    notes?: string;
  }): Promise<Company> => {
    const response = await api.post("/company", data);
    return response.data;
  },

  updateCompany: async (
    id: string,
    data: {
      name?: string;
      ctc?: number;
      base?: number;
      medical?: number;
      joiningBonus?: number;
      relocationBonus?: number;
      otherBenefits?: number;
      notes?: string;
    }
  ): Promise<Company> => {
    const response = await api.put(`/company/${id}`, data);
    return response.data;
  },

  deleteCompany: async (id: string): Promise<void> => {
    await api.delete(`/company/${id}`);
  },
};

// Company Salary API
export const companySalaryAPI = {
  getSalaryTransactions: async (
    companyId: string,
    params?: {
      month?: number;
      year?: number;
    }
  ): Promise<CompanySalaryTransaction[]> => {
    const response = await api.get(`/company-salary/company/${companyId}`, { params });
    return response.data;
  },

  getSalarySummary: async (companyId: string): Promise<CompanySalarySummary> => {
    const response = await api.get(`/company-salary/company/${companyId}/summary`);
    return response.data;
  },

  createSalaryTransaction: async (data: {
    company: string;
    amount: number;
    month: number;
    year: number;
    taxDeducted?: number;
    notes?: string;
  }): Promise<CompanySalaryTransaction> => {
    const response = await api.post("/company-salary", data);
    return response.data;
  },

  deleteSalaryTransaction: async (id: string): Promise<void> => {
    await api.delete(`/company-salary/${id}`);
  },
};

// Investment Platforms API
export const investmentPlatformsAPI = {
  getPlatforms: async (): Promise<InvestmentPlatform[]> => {
    const response = await api.get("/investment-platforms");
    return response.data;
  },

  getPlatform: async (id: string): Promise<InvestmentPlatform> => {
    const response = await api.get(`/investment-platforms/${id}`);
    return response.data;
  },

  createPlatform: async (data: {
    name: string;
    type: "broker" | "stock_broker" | "bank" | "exchange" | "wallet" | "crypto_exchange" | "other";
    accountNumber?: string;
    notes?: string;
    walletBalance?: number;
  }): Promise<InvestmentPlatform> => {
    const response = await api.post("/investment-platforms", data);
    return response.data;
  },

  updatePlatform: async (
    id: string,
    data: {
      name?: string;
      type?: "broker" | "stock_broker" | "bank" | "exchange" | "wallet" | "crypto_exchange" | "other";
      accountNumber?: string;
      notes?: string;
      walletBalance?: number;
    }
  ): Promise<InvestmentPlatform> => {
    const response = await api.put(`/investment-platforms/${id}`, data);
    return response.data;
  },

  deletePlatform: async (id: string): Promise<void> => {
    await api.delete(`/investment-platforms/${id}`);
  },

  addFunds: async (
    id: string,
    data: {
      amount: number;
      fromBankAccount?: string;
    }
  ): Promise<InvestmentPlatform> => {
    const response = await api.post(`/investment-platforms/${id}/add-funds`, data);
    return response.data;
  },

  withdrawFunds: async (
    id: string,
    data: {
      amount: number;
      toBankAccount?: string;
    }
  ): Promise<InvestmentPlatform> => {
    const response = await api.post(`/investment-platforms/${id}/withdraw-funds`, data);
    return response.data;
  },
};

// Investments API
export const investmentsAPI = {
  getInvestments: async (params?: {
    platform?: string;
    type?: string;
    status?: string;
  }): Promise<Investment[]> => {
    const response = await api.get("/investments", { params });
    return response.data;
  },

  getInvestmentsByPlatform: async (platformId: string): Promise<Investment[]> => {
    const response = await api.get(`/investments?platform=${platformId}`);
    return response.data;
  },

  getInvestment: async (id: string): Promise<Investment> => {
    const response = await api.get(`/investments/${id}`);
    return response.data;
  },

  getInvestmentStats: async (): Promise<InvestmentStats> => {
    const response = await api.get("/investments/stats/summary");
    return response.data;
  },

  createInvestment: async (data: {
    platform: string;
    type: "stocks" | "fno" | "mutual_funds" | "bonds" | "fixed_deposits" | "crypto" | "etf";
    companyName: string;
    amount: number;
    quantity?: number;
    purchasePrice?: number;
    currentPrice?: number;
    purchaseDate: string;
    maturityDate?: string;
    interestRate?: number;
    notes?: string;
    deductFromBank?: boolean;
    bankAccount?: string;
    deductFromPlatform?: boolean;
  }): Promise<Investment> => {
    const response = await api.post("/investments", data);
    return response.data;
  },

  updateInvestment: async (
    id: string,
    data: {
      companyName?: string;
      amount?: number;
      quantity?: number;
      purchasePrice?: number;
      currentPrice?: number;
      purchaseDate?: string;
      maturityDate?: string;
      interestRate?: number;
      status?: "active" | "sold" | "matured" | "withdrawn";
      notes?: string;
    }
  ): Promise<Investment> => {
    const response = await api.put(`/investments/${id}`, data);
    return response.data;
  },

  withdrawInvestment: async (
    id: string,
    data: {
      bankAccount: string;
      withdrawalAmount: number;
    }
  ): Promise<Investment> => {
    const response = await api.post(`/investments/${id}/withdraw`, data);
    return response.data;
  },

  deleteInvestment: async (id: string): Promise<void> => {
    await api.delete(`/investments/${id}`);
  },
};

export default api;
